"use client";

import React from "react";
import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Send,
  Database,
  ChevronDown,
  ChevronRight,
  X,
  Table,
} from "lucide-react";
import Image from "next/image";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useRouter } from "next/router";
import { reconChatInputHoverMessage } from "../../messageTemplates/messages";

export interface ContextData {
  id?: string; // Keep optional id for UI purposes
  content: string;
  displaySummary?: {
    tableName: string;
    rowCount: number;
    rowRange: string;
  };
}

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  context?: ContextData[];
}

interface ChatInterfaceProps {
  className?: string;
  /** Recon-playground: message only; streaming via invoke (conversationId, message, invoke_type). */
  onSendMessage?: (message: string) => Promise<string> | Promise<void> | void;
  isLoading?: boolean;
  externalData?: string;
  loadingMessage?: string;
}

// Define a new interface for structured table selection data
interface TableSelectionData {
  tableName: string;
  rows: string | number | number[]; // Can be "1-5", 2, or [1,2,3,5]
  columns: string | number | number[]; // Can be "0-3", 1, or [0,1,3]
  totalRows?: number;
  totalColumns?: number;
}

const ChatInterface = forwardRef<any, ChatInterfaceProps>(
  (
    {
      className = "",
      onSendMessage,
      isLoading = false,
      externalData,
      loadingMessage = " ",
    },
    ref,
  ) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(true);
    const [contextData, setContextData] = useState<ContextData[]>([]);
    const [expandedContexts, setExpandedContexts] = useState<
      Record<string, boolean>
    >({});
    const [hasStartedTyping, setHasStartedTyping] = useState(false);
    const [showInputHoverTooltip, setShowInputHoverTooltip] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const chatInterfaceRef = useRef<any>(null);
    const isSendingMessage = useRef(false);
    const prevLoadingRef = useRef(isLoading);
    const router = useRouter();

    // Check if we're on a recon page
    const isReconPage = router.pathname?.startsWith("/recon") || false;

    const scrollToBottom = () => {
      // Use setTimeout to ensure DOM is updated before scrolling
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({
            behavior: "smooth",
            block: "end",
            inline: "nearest",
          });
        }
      }, 100);
    };

    // Scroll to bottom when messages change
    useEffect(() => {
      scrollToBottom();
    }, [messages]);

    // Also scroll to bottom when loading state changes
    useEffect(() => {
      if (isLoading) {
        scrollToBottom();
      }
    }, [isLoading]);

    // Focus input when loading finishes (input is disabled while loading so focus only works after)
    useEffect(() => {
      const wasLoading = prevLoadingRef.current;
      prevLoadingRef.current = isLoading;
      if (wasLoading && !isLoading) {
        const id = setTimeout(() => inputRef.current?.focus(), 50);
        return () => clearTimeout(id);
      }
    }, [isLoading]);

    // Reset hasStartedTyping when messages are cleared (new chat)
    useEffect(() => {
      if (messages.length === 0) {
        setHasStartedTyping(false);
      }
    }, [messages.length]);

    // Simplified function to add any data to context
    const addToContext = (content: string) => {
      const newContext: ContextData = {
        content,
      };
      setContextData((prev) => {
        const updated = [...prev, newContext];
        return updated;
      });
    };

    // Handle external data from sheets
    useEffect(() => {
      // Don't add context if we're currently sending a message
      if (isSendingMessage.current) return;

      if (externalData && externalData.trim()) {
        try {
          const parsedData = JSON.parse(externalData);
          // Only store the essential data
          const newContext: ContextData = {
            content: JSON.stringify(parsedData.detailedData), // Only store the actual data
            displaySummary: parsedData.displaySummary, // Keep display summary for UI
          };
          setContextData((prev) => {
            const updated = [...prev, newContext];
            return updated;
          });
        } catch (error) {
          console.error("Error parsing externalData, using fallback:", error);
          // Fallback for non-JSON data
          addToContext(externalData);
        }
      }
    }, [externalData]);

    // Function to remove context item
    const removeContext = (contextId: string) => {
      setContextData((prev) => prev.filter((item) => item.id !== contextId));
    };

    const clearAllContext = () => setContextData([]);

    // Function to handle input changes and clear pending data updates
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);

      // Do NOT clear pending data updates on typing. We'll clear only on send or explicit clear.
      if (!hasStartedTyping && newValue.trim() !== "") {
        setHasStartedTyping(true);
      }
    };

    // Function to toggle context expansion for a message
    const toggleContextExpansion = (messageId: string) => {
      setExpandedContexts((prev) => ({
        ...prev,
        [messageId]: !prev[messageId],
      }));
    };

    const handleSendMessage = async (messageText?: string) => {
      const userInput = messageText || inputValue.trim();

      // If no user input and no context data, don't send anything
      if ((!userInput || userInput === "") && contextData.length === 0) return;

      if (isLoading) return;

      // Set flag to prevent externalData useEffect from running
      isSendingMessage.current = true;

      const newMessage: Message = {
        id: Date.now().toString(),
        text: userInput || "Analyze this data",
        isUser: true,
        timestamp: new Date(),
        context: contextData.length > 0 ? [...contextData] : undefined,
      };

      setMessages((prev) => {
        const updatedMessages = [...prev, newMessage];
        return updatedMessages;
      });
      setInputValue("");
      setShowSuggestions(false);

      // Reset textarea height to original size and refocus
      if (inputRef.current) {
        inputRef.current.style.height = "1.5rem";
        // Use setTimeout to ensure the input is focused after the state updates
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      }

      scrollToBottom();
      clearAllContext();

      // Recon-playground: message only; response streamed via invoke (recon-playground invoke uses conversationId + message + invoke_type).
      if (onSendMessage) {
        try {
          const response = await onSendMessage(userInput);
          if (response && typeof response === "string") {
            addAIMessage(response);
          }
        } catch (error) {
          console.error("Error in onSendMessage:", error);
          addAIMessage(
            "Sorry, I encountered an error while processing your message. Please try again.",
          );
        }
      }

      // Reset the flag
      isSendingMessage.current = false;
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        // Use current value from DOM to avoid stale React state when Enter fires before state update
        const currentValue =
          (e.target as HTMLTextAreaElement).value?.trim() ?? "";
        handleSendMessage(currentValue || undefined);
      }
    };

    const addAIMessage = (text: string) => {
      const aiMessage: Message = {
        id: Date.now().toString(),
        text,
        isUser: false,
        timestamp: new Date(),
      };
      setMessages((prev) => {
        const updatedMessages = [...prev, aiMessage];
        return updatedMessages;
      });

      // Scroll to bottom when AI message is added
      scrollToBottom();
    };

    // Add method to clear all messages
    const clearMessages = () => {
      setMessages([]);
    };

    // Add message to chat history
    const addMessage = (message: Message) => {
      setMessages((prev) => [...prev, message]);
    };

    // Expose functions via ref
    useImperativeHandle(
      ref,
      () => ({
        addToContext,
        addMessage,
        clearMessages,
        getContextData: () => contextData,
      }),
      [], // Remove contextData from dependencies to prevent unnecessary re-renders
    );

    return (
      <div
        ref={chatInterfaceRef}
        className={`w-full h-full flex flex-col bg-[#ffffff] ${className}`}
      >
        {/* Header */}
        <div className="bg-[#ffffff] border-b border-solid border-[#ECECEC] px-4 flex items-center">
          <div className="flex flex-row items-center gap-4 h-[4.8rem] mt-2 py-0.5">
            <Image
              src="/onecaplogo.svg"
              alt="OneCap Logo"
              width={100}
              height={100}
              priority
              className="w-36 h-32"
              style={{ objectFit: "contain" }}
            />
            <span className="text-[1.25rem] font-semibold italic text-[#01187D] font-lato">
              Recon Agent
            </span>
          </div>
        </div>

        {/* Chat Messages Area - Dynamic height based on context and page type */}
        <div
          className="flex-1 overflow-y-auto overflow-x-hidden scroll-smooth scrollbar-hide bg-[#ffffff] flex flex-col"
          style={{
            // Input area is always rendered; on recon pages it's disabled (not hidden).
            height:
              contextData.length > 0
                ? "calc(100% - 80px)" // Header + context + input area
                : "calc(100% - 60px)", // Header + input area
          }}
        >
          {messages.length > 0 ? (
            <div className="flex-1 flex flex-col justify-end">
              <div className="flex flex-col gap-2 p-4">
                {messages.map((message, index) => {
                  // Skip empty assistant messages (e.g. processing/polling placeholder) so we don't render an empty bubble
                  if (
                    !message.isUser &&
                    (!message.text || message.text.trim() === "")
                  ) {
                    return null;
                  }
                  return (
                    <div
                      key={message.id}
                      className={`mb-3 flex ${
                        message.isUser ? "justify-end" : "justify-start"
                      } w-full`}
                    >
                      {message.isUser ? (
                        // User Message - aligned to right side of input box
                        <div className="w-[90%] flex justify-end">
                          <div
                            className={`${
                              message.context && message.context.length > 0
                                ? "w-full" // Full width when has context for expansion
                                : "max-w-[90%]" // Normal chat width when no context
                            } bg-[#01187D] text-white p-4 rounded-2xl break-words drop-shadow-sm overflow-hidden mr-2`}
                          >
                            <span className="text-[1.125rem] text-[#FFFFFF] chat-bot-text whitespace-pre-wrap">
                              {message.text}
                            </span>

                            {/* Context indicator and dropdown for user messages */}
                            {message.context && message.context.length > 0 && (
                              <div className="mt-1 pt-1 border-t border-gray-200/30">
                                <button
                                  onClick={() =>
                                    toggleContextExpansion(message.id)
                                  }
                                  className="flex items-center gap-1 py-1 px-2 rounded text-blue-950 transition-colors text-[0.875rem]"
                                >
                                  <Database className="w-3 h-3" />
                                  <span>
                                    {message.context.length > 1
                                      ? `Context (${message.context.length} items)`
                                      : "Context"}
                                  </span>
                                  {expandedContexts[message.id] ? (
                                    <ChevronDown className="w-3 h-3" />
                                  ) : (
                                    <ChevronRight className="w-3 h-3" />
                                  )}
                                </button>

                                {/* Expanded context */}
                                {expandedContexts[message.id] && (
                                  <div className="mt-2 space-y-1">
                                    {message.context.map((context) => (
                                      <div
                                        key={context.id}
                                        className="p-2 rounded bg-blue-700/50 text-blue-50 text-[0.875rem]"
                                      >
                                        {context.displaySummary ? (
                                          <span>
                                            {context.displaySummary.tableName} (
                                            {context.displaySummary.rowRange})
                                          </span>
                                        ) : (
                                          <span className="whitespace-pre-wrap break-words">
                                            {typeof context.content === "string"
                                              ? context.content.length > 100
                                                ? context.content.substring(
                                                    0,
                                                    100,
                                                  ) + "..."
                                                : context.content
                                              : "Content not available"}
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        // AI Response Message - aligned with input box left edge
                        <div className="w-[90%] flex flex-row items-start gap-3">
                          <div className="flex-1 w-full">
                            <div className="w-full relative">
                              <div className="w-full flex flex-col px-3 py-2 rounded-2xl bg-[#EDF4FA] drop-shadow-lg">
                                <div className="text-[1.125rem] chat-bot-text pr-3 py-2 text-[#040404] whitespace-pre-wrap break-words hyphens-auto w-full">
                                  <div className="chat-bot-text">
                                    <ReactMarkdown
                                      remarkPlugins={[remarkGfm]}
                                      components={{
                                        table: ({ node, ...props }) => (
                                          <div className="markdown-table-wrapper">
                                            <table {...props} />
                                          </div>
                                        ),
                                      }}
                                    >
                                      {message.text}
                                    </ReactMarkdown>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {isLoading && (
                  <div className="mb-3 flex justify-start w-full">
                    <div className="w-[90%] flex flex-row items-start gap-3">
                      <div className="flex flex-col items-start gap-1 mt-1 flex-1">
                        <div className="w-full h-auto flex flex-col gap-4">
                          <div className="w-full flex flex-col px-3 py-2 rounded-2xl  drop-shadow-lg">
                            {loadingMessage && loadingMessage.trim() !== "" && (
                              <span className="text-[0.875rem] text-gray-500">
                                {loadingMessage}
                              </span>
                            )}
                            <div className="flex items-center gap-2 pr-3 py-2">
                              <div className="flex space-x-1">
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                <div
                                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                  style={{ animationDelay: "0.1s" }}
                                ></div>
                                <div
                                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                  style={{ animationDelay: "0.2s" }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>
          ) : (
            /* Empty State with Skeleton */
            // <ChatSkeleton />
            <></>
          )}
        </div>

        {/* Context Section - Fixed position above input */}
        {contextData.length > 0 && (
          <div className="border-t border-gray-200 bg-gray-50 p-3 max-h-24 overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[0.875rem] font-medium text-gray-700 uppercase tracking-wide">
                Selected Data ({contextData.length})
              </span>
              <div
                onClick={clearAllContext}
                className="text-[0.875rem] text-gray-700 hover:text-red-600 transition-colors cursor-pointer"
                title="Clear all context"
              >
                Clear All
              </div>
            </div>
            <div className="space-y-1">
              {contextData.map((context) => (
                <div
                  key={context.id}
                  className="flex flex-row justify-between rounded border border-gray-200 p-2 text-[0.875rem]"
                >
                  <div className="flex items-center gap-2">
                    <Table className="w-4 h-4 text-gray-600" />
                    {context.displaySummary ? (
                      <span className="text-gray-700">
                        {context.displaySummary.tableName} (
                        {context.displaySummary.rowRange})
                      </span>
                    ) : (
                      <div className="max-h-10 overflow-y-auto font-mono bg-gray-50 p-1 rounded text-[0.875rem]">
                        <div className="line-clamp-2 text-gray-700">
                          {context.content}
                        </div>
                      </div>
                    )}
                  </div>

                  <div
                    onClick={() => removeContext(context.id || "")}
                    className="text-gray-700 hover:text-red-500 transition-colors cursor-pointer"
                    title="Remove this context"
                  >
                    <X className="w-3 h-3" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input Area - Disabled (but visible) on recon pages */}
        <div className="border-t border-solid drop-shadow-md shadow-midnightblue border-[#ECECEC] p-4 bg-white">
          <div className="relative">
            <div className="relative w-[90%]">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={
                  contextData.length > 0
                    ? "Ask a question about this data..."
                    : "Ask anything..."
                }
                disabled={isLoading}
                rows={1}
                className={`w-full px-4 py-3 border border-solid border-blue-900 rounded-lg focus:outline-none text-[1rem] resize-none overflow-y-auto`}
                style={{
                  height: "1.5rem",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  },
);

ChatInterface.displayName = "ChatInterface";

export default ChatInterface;
